import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

// HC-JOBS-003: pattern-mirrored from mobile's edit_profile_screen.dart
// _buildSkillsSection() — search-as-you-type input, filtered suggestion
// dropdown, tap-to-add, deletable chips for selected skills. No direct code
// reuse is possible (Flutter vs React), so this is a from-scratch
// reimplementation of the same interaction pattern, backed by the same
// skills/{industrySlug} Firestore taxonomy (4 industry docs, 77 skills).
//
// web-admin/ has no existing tag/chip/multi-select component or UI library
// to extend (confirmed by the HC-JOBS-003 investigation) — every style here
// is a local inline object, matching the convention already used throughout
// PostJobPage.js / EditJobPage.js.

const INDUSTRY_SLUGS = ['construction', 'warehouse', 'hospitality', 'general'];

const labelStyle = { display: 'block', marginBottom: '0.5rem', fontWeight: '500', fontSize: '14px' };
const helpStyle = { fontSize: '12px', color: '#6b7280', marginTop: '4px' };
const inputStyle = {
  width: '100%',
  padding: '0.5rem',
  border: '1px solid #d1d5db',
  borderRadius: '4px',
  fontSize: '16px',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const suggestionListStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: '4px',
  marginTop: '4px',
  maxHeight: '200px',
  overflowY: 'auto',
  backgroundColor: 'white',
  boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
};

const suggestionItemStyle = {
  padding: '0.5rem 0.75rem',
  fontSize: '14px',
  cursor: 'pointer',
  borderBottom: '1px solid #f3f4f6',
};

const chipRowStyle = { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '0.75rem' };

const chipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  backgroundColor: '#f0fdfa',
  border: '1px solid #99f6e4',
  color: '#0f766e',
  borderRadius: '999px',
  padding: '4px 6px 4px 12px',
  fontSize: '13px',
  fontWeight: '500',
};

const chipRemoveStyle = {
  cursor: 'pointer',
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(15, 118, 110, 0.12)',
  fontSize: '12px',
  lineHeight: 1,
};

export default function SkillsPicker({ value, onChange }) {
  const [allSkills, setAllSkills] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'skills'));
        const merged = [];
        snap.forEach((docSnap) => {
          if (!INDUSTRY_SLUGS.includes(docSnap.id)) return;
          const data = docSnap.data();
          if (Array.isArray(data.skills)) {
            merged.push(...data.skills);
          }
        });
        if (!cancelled) setAllSkills(merged);
      } catch (err) {
        console.error('SkillsPicker: failed to load skills collection', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredSuggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return allSkills
      .filter((s) => !value.includes(s.id) && s.name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, allSkills, value]);

  const skillName = (id) => {
    const skill = allSkills.find((s) => s.id === id);
    return skill ? skill.name : id;
  };

  const addSkill = (id) => {
    onChange([...value, id]);
    setQuery('');
  };

  const removeSkill = (id) => {
    onChange(value.filter((s) => s !== id));
  };

  return (
    <div>
      <label style={labelStyle}>Skills <span style={{ fontWeight: '400', color: '#6b7280' }}>(optional)</span></label>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search skills to add (e.g. forklift, drywall)…"
        style={inputStyle}
      />
      {filteredSuggestions.length > 0 && (
        <div style={suggestionListStyle}>
          {filteredSuggestions.map((skill) => (
            <div
              key={skill.id}
              style={suggestionItemStyle}
              onClick={() => addSkill(skill.id)}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
            >
              {skill.name}
            </div>
          ))}
        </div>
      )}
      {value.length > 0 && (
        <div style={chipRowStyle}>
          {value.map((id) => (
            <span key={id} style={chipStyle}>
              {skillName(id)}
              <span style={chipRemoveStyle} onClick={() => removeSkill(id)}>×</span>
            </span>
          ))}
        </div>
      )}
      <div style={helpStyle}>Help workers find this job by tagging relevant skills</div>
    </div>
  );
}
